import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { mergeRefs } from '@cbhq/cds-common/utils/mergeRefs';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Dropdown } from '@cbhq/cds-web/dropdown/Dropdown';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Box, Divider } from '@cbhq/cds-web/layout';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import type { DropdownElementProps } from '../../types';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSyncBrowserTab } from '../hooks/useSyncBrowserTab';
import { useClientProps } from '../providers/ClientPropsProvider';
import { ProfileDropdownControlProvider } from '../providers/ProfileDropdownControlProvider';

import { ProfileDropdownButton } from './ProfileDropdownButton';
import { ProfileDropdownContent } from './ProfileDropdownContent';

const messages = defineMessages({
  closeProfileMenu: {
    defaultMessage: 'Close Profile Menu',
    id: 'ProfileDropdown.closeProfileMenu',
    description: 'Accessibility label for the close profile menu button.',
  },
});

const mobileContentWrapperStyle: CSSProperties = {
  scrollbarWidth: 'none',
};

export function ProfileDropdown() {
  const { logEvent } = useAnalytics();
  const { profileMenuRef, onCloseProfileMenu, onOpenProfileMenu } = useClientProps();
  const { isPhone } = useBreakpoints();
  const { formatMessage } = useIntl();
  useSyncBrowserTab();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const documentScrollDefaultRef = useRef(document.documentElement.style.overflow);
  const internalDropdownRef = useRef<DropdownElementProps>(null);

  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    if (!visible && document.documentElement.style.overflow !== documentScrollDefaultRef.current) {
      // Restore the default overflow style
      document.documentElement.style.overflow = documentScrollDefaultRef.current;
    } else if (isPhone && visible) {
      // Prevent background scrolling when the modal is open
      document.documentElement.style.overflow = 'hidden';
    }
  }, [isPhone, visible]);

  const handleOpen = useCallback(() => {
    onOpenProfileMenu?.();

    logEvent('profile_menu_open', {
      action: ActionType.click,
      componentType: ComponentType.button,
    });
  }, [logEvent, onOpenProfileMenu]);

  const handleClose = useCallback(() => {
    buttonRef?.current?.focus();
    onCloseProfileMenu?.();

    logEvent('profile_menu_close', {
      action: ActionType.click,
      componentType: ComponentType.button,
    });
  }, [logEvent, onCloseProfileMenu]);

  const handlePhonePress = useCallback(() => {
    if (visible) {
      handleClose();
      setVisible(false);
    } else {
      handleOpen();
      setVisible(true);
    }
  }, [visible, handleClose, handleOpen]);

  useEffect(() => {
    // Make the openMenu and closeMenu functions available for mobile
    if (isPhone && profileMenuRef?.current) {
      profileMenuRef.current.openMenu = handlePhonePress;
      profileMenuRef.current.closeMenu = handlePhonePress;
    }
  }, [isPhone, profileMenuRef, handlePhonePress]);

  // `profileMenuRef` is a host-supplied prop and most consumers don't pass one
  // (e.g. apps/workspace), so anything that needs to close this dropdown from
  // deep inside its own content (like ManagedByBanner) can't rely on it —
  // that ref being absent would make the close a silent no-op. `closeMenu`
  // here is always backed by `internalDropdownRef`/the phone `visible` state,
  // regardless of whether the host opted into the ref.
  const closeMenu = useCallback(() => {
    if (isPhone) {
      if (visible) {
        handleClose();
        setVisible(false);
      }
      return;
    }
    internalDropdownRef.current?.closeMenu();
  }, [isPhone, visible, handleClose]);

  const dropdownRef = useMemo(
    () => mergeRefs<DropdownElementProps>(profileMenuRef, internalDropdownRef),
    [profileMenuRef],
  );

  const modalContent = useMemo(
    () => (
      <VStack
        position="fixed"
        width="100vw"
        height="100vh"
        zIndex={zIndex.portal}
        left={0}
        top={0}
        ref={profileMenuRef as unknown as React.RefObject<HTMLDivElement>}
        key="profile-dropdown"
        background="bg"
      >
        <HStack width="100%" paddingY={1} paddingX={2} justifyContent="flex-end">
          <IconButton
            accessibilityLabel={formatMessage(messages.closeProfileMenu)}
            name="close"
            color="fg"
            onClick={handlePhonePress}
            transparent
          />
        </HStack>
        <Divider />
        <Box overflow="scroll" padding={0} style={mobileContentWrapperStyle}>
          <ProfileDropdownContent />
        </Box>
      </VStack>
    ),
    [handlePhonePress, profileMenuRef, formatMessage],
  );

  if (isPhone) {
    // Mobile modal
    return (
      <ProfileDropdownControlProvider closeMenu={closeMenu}>
        <ProfileDropdownButton buttonRef={buttonRef} onPress={handlePhonePress} />
        {visible ? modalContent : null}
      </ProfileDropdownControlProvider>
    );
  }

  return (
    <ProfileDropdownControlProvider closeMenu={closeMenu}>
      <Dropdown
        enableMobileModal
        onOpenMenu={handleOpen}
        onCloseMenu={handleClose}
        content={<ProfileDropdownContent />}
        ref={dropdownRef}
        maxHeight="100%"
        showOverlay
      >
        <ProfileDropdownButton buttonRef={buttonRef} />
      </Dropdown>
    </ProfileDropdownControlProvider>
  );
}
